import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Heart, Users, BookOpen, Sparkles, MapPin, Smile } from 'lucide-react'

const features = [
  {
    icon: Heart,
    title: 'Warm Welcome',
    description: 'Experience genuine hospitality from our church family',
    color: 'from-rose-500 to-pink-500',
    bgColor: 'bg-rose-100',
  },
  {
    icon: Users,
    title: 'Find Your Tribe',
    description: 'Connect with others through small groups and ministries',
    color: 'from-primary-500 to-blue-500',
    bgColor: 'bg-primary-100',
  },
  {
    icon: BookOpen,
    title: 'Grow in Faith',
    description: 'Deepen your relationship with God through biblical teaching',
    color: 'from-secondary-500 to-purple-500',
    bgColor: 'bg-secondary-100',
  },
]

const NewHereBanner = () => {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary-50/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-secondary-50/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="bg-gradient-to-br from-light-100 to-white rounded-[2.5rem] p-8 md:p-12 lg:p-16 relative overflow-hidden shadow-soft border border-white">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              animate={{ y: [-20, 20, -20], x: [-10, 10, -10] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-10 right-10 w-32 h-32 bg-secondary-200/50 rounded-full blur-2xl"
            />
            <motion.div
              animate={{ y: [20, -20, 20], x: [10, -10, 10] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-10 left-10 w-40 h-40 bg-accent-200/50 rounded-full blur-2xl"
            />
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary-200/30 rounded-full blur-3xl"
            />
          </div>

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Badge */}
              <motion.span
                className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-5 py-2.5 rounded-full text-sm font-bold mb-6 shadow-lg shadow-secondary-500/25"
                whileHover={{ scale: 1.05 }}
              >
                <Smile className="w-4 h-4" />
                First Time Visitor?
              </motion.span>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-dark-100 mb-6 leading-tight">
                We'd Love to <br />
                <span className="gradient-text">Meet You!</span>
              </h2>

              <p className="text-lg text-dark-300 mb-8 leading-relaxed">
                Whether you're exploring faith for the first time or looking for a new church home,
                we're excited to welcome you. Discover what makes our church family special.
              </p>

              {/* Quick Info */}
              <div className="flex items-center gap-4 mb-8 p-4 bg-white rounded-2xl shadow-soft">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-orange-500 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-dark-100">Join Us This Sunday!</p>
                  <p className="text-sm text-dark-300">Sunday Worship at 9:00 AM</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/new-here">
                  <motion.button
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-bold shadow-lg shadow-secondary-500/25"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    I'm New Here
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
                <Link to="/plan-your-visit">
                  <motion.button
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-primary-500 text-primary-600 font-bold hover:bg-primary-50 transition-colors"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Sparkles className="w-5 h-5" />
                    Plan Your Visit
                  </motion.button>
                </Link>
              </div>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-5"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ x: 10, scale: 1.02 }}
                  className="flex gap-5 items-start bg-white rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all border border-gray-100 cursor-pointer group"
                >
                  <motion.div
                    className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <feature.icon className="w-7 h-7 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-100 mb-1 group-hover:text-primary-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-dark-300">{feature.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-primary-500 group-hover:translate-x-1 transition-all ml-auto self-center flex-shrink-0" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default NewHereBanner
